import React, { createContext, useContext, ReactNode, useRef } from "react";
import { notification } from "antd";

type NotificationType = "success" | "error";

interface NotificationContextProps {
  notify: (type: NotificationType, message: string, description: string) => void;
}

interface NotificationProviderProps {
  children: ReactNode;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [api, contextHolder] = notification.useNotification();
  const notificationCache = useRef<Set<string>>(new Set());

  const notify = (type: NotificationType, message: string, description: string) => {
    // 通知の一意キーを生成（タイプ、メッセージ、説明を組み合わせ）
    const notificationKey = `${type}-${message}-${description}`;
    
    // 既に同じ通知が表示されている場合はスキップ
    if (notificationCache.current.has(notificationKey)) {
      return;
    }

    // キャッシュに追加
    notificationCache.current.add(notificationKey);

    api[type]({
      message: message,
      description: description,
      placement: "top",
      duration: 10,
      onClose: () => {
        // 通知が閉じられたらキャッシュから削除
        notificationCache.current.delete(notificationKey);
      },
    });
  };

  // return キーワードとフラグメントを使用
  return (
    <>
      <NotificationContext.Provider value={{ notify }}>
        {contextHolder}
        {children}
      </NotificationContext.Provider>
    </>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};

